import { useMemo } from 'react';
import { useGetOfferingsQuery } from 'store/api/offerings/offeringsApiSlice';
import { OfferingResponse } from 'store/api/offerings/offeringsApiTypes';
import { usePollingOnError } from 'shared/hooks/usePollingOnError';

/**
 * useOfferings
 * Fetches GET /domains/:domainId/offerings and keeps it fresh while the run is
 * still producing offerings: the backend answers 202 with an empty list until
 * they exist, then 200 with the data — so `usePollingOnError` refetches every
 * 4s for as long as the response says 202, the same way the report-section
 * hooks do.
 */
export const useOfferings = (
  domainId: string | undefined,
  options?: { skip?: boolean }
) => {
  const queryParams = useMemo<string | null>(
    () => (domainId && !options?.skip ? domainId : null),
    [domainId, options?.skip]
  );

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch: refetchOfferings,
  } = useGetOfferingsQuery(queryParams as string, {
    skip: !queryParams,
    refetchOnMountOrArgChange: true,
  });

  usePollingOnError(queryParams, error, refetchOfferings, data);

  const offerings: OfferingResponse[] = data?.data ?? [];
  const statusCode = data?.statusCode;

  return {
    offerings,
    statusCode,
    // 202 = the run has not produced offerings yet, polling is in flight.
    isOfferingsPending: statusCode === 202,
    hasOfferings: offerings.length > 0,
    isLoading,
    isFetching,
    error,
    queryParams,
    refetchOfferings,
  };
};
